import {Map as GoogleMap} from '@vis.gl/react-google-maps';
import {motion} from 'framer-motion';
import i18n from 'i18next';
import {Box, Integer, Show, ValueByDevice} from 'platform/foundation';
import styled from 'styled-components';

import {equals, isNil, last} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {Button} from '../../../../../components/Button/Button';
import {useMapIntro} from '../hooks/useMapIntro';
import {useRouteAnimation} from '../hooks/useRouteAnimation';
import {getDrawnPath} from '../utils/getDrawnPath';
import {getRouteBounds, RoutePoint} from '../utils/getRouteBounds';
import {routeMapStyles} from '../utils/routeMapStyles';
import {RouteLine} from './RouteLine';
import {RouteMarkers} from './RouteMarkers';

// A wide strip below the test drive results, lower on phones where the section is narrow.
const MAP_HEIGHT: ValueByDevice<Integer> = [55, 70, 70, 70];
// Share of the map that has to be on screen before the route starts drawing.
const VIEWPORT_VISIBLE_AMOUNT = 0.5;
const MAP_PADDING = 48;
// Street level, so a single place still shows its surroundings instead of the maximum zoom.
const SINGLE_PLACE_ZOOM = 16;
const MAP_FADE_IN_MS = 300;

// Framed like the attributes of the section, so the map reads as a part of the card.
const MapFrame = styled(motion.div)`
  position: relative;
  height: 100%;
  border: 1px solid ${({theme}) => theme.colors.border.emphasisDefault};
  border-radius: ${({theme}) => theme.radii.medium};
  overflow: hidden;
`;

type AnimatedRouteMapProps = TestIdProps & {
  /** The smoothed route. */
  path: RoutePoint[];
  /** Distance in metres from the start to every point of `path`. */
  distances: number[];
};

export function AnimatedRouteMap(props: AnimatedRouteMapProps) {
  // A drive without movement records a single place, which a line cannot show, so it gets a marker.
  const singlePlace = props.path.every((point) => equals(point, props.path[0]))
    ? props.path[0]
    : null;
  const totalDistance = last(props.distances) ?? 0;
  const animation = useRouteAnimation(totalDistance);
  const bounds = getRouteBounds(props.path);
  const hasCameraIntro = isNil(singlePlace) && !animation.shouldReduceMotion;

  const intro = useMapIntro(hasCameraIntro, animation.start);

  const drawnPath = isNil(animation.drawnDistance)
    ? []
    : getDrawnPath(props.path, props.distances, animation.drawnDistance);

  return (
    <Box height={MAP_HEIGHT}>
      {/* The route is drawn once the map is really on screen, so the user sees it being drawn. */}
      <MapFrame
        viewport={{once: true, amount: VIEWPORT_VISIBLE_AMOUNT}}
        onViewportEnter={intro.handleViewportEnter}
        data-testid={props['data-testid']}
      >
        <Show when={bounds}>
          {(routeBounds) => (
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              opacity={intro.isMapHidden ? 0 : 1}
              transition={`opacity ${MAP_FADE_IN_MS}ms ease-out`}
            >
              <GoogleMap
                {...(singlePlace
                  ? {defaultCenter: singlePlace, defaultZoom: SINGLE_PLACE_ZOOM}
                  : {defaultBounds: {...routeBounds, padding: MAP_PADDING}})}
                styles={routeMapStyles}
                gestureHandling="cooperative"
                disableDefaultUI
                fullscreenControl
                clickableIcons={false}
                onIdle={intro.handleIdle}
              >
                <Show when={isNil(singlePlace)}>
                  <RouteLine pointCount={props.path.length} drawnPath={drawnPath} />
                </Show>
                <RouteMarkers
                  path={props.path}
                  drawnPath={drawnPath}
                  isFinished={animation.isFinished}
                />
              </GoogleMap>
            </Box>
          )}
        </Show>
        <Show when={animation.canReplay}>
          {/* Above the Google logo in the bottom left corner. */}
          <Box position="absolute" left={3} bottom={8} borderRadius="large" boxShadow="elevation_2">
            <Button
              variant="inverted"
              size="small"
              leftIcon="replay"
              title={i18n.t('testDrive.replayRoute')}
              onClick={animation.start}
              data-testid={suffixTestId('replay', props)}
            />
          </Box>
        </Show>
      </MapFrame>
    </Box>
  );
}
